import React from "react";
import { Helmet } from "react-helmet";
import {
  Phone,
  Clock,
  CheckCircle,
  Shield,
  Headphones,
  Globe,
  Plane,
  Users,
  Star,
  AlertCircle,
} from "lucide-react";
import "./ReservaDeAerolineas.css";

const TFN = "1-855-397-5016";
const TFN_CLEAN = "18553975016";

const ReservaDeAerolineas = () => {
  const aerolineas = [
    { nombre: "American Airlines", codigo: "AA", color: "#0078D2" },
    { nombre: "Delta Air Lines",   codigo: "DL", color: "#003A70" },
    { nombre: "United Airlines",   codigo: "UA", color: "#002244" },
    { nombre: "Southwest Airlines",codigo: "WN", color: "#304CB2" },
    { nombre: "JetBlue Airways",   codigo: "B6", color: "#003876" },
    { nombre: "Alaska Airlines",   codigo: "AS", color: "#01426A" },
    { nombre: "Spirit Airlines",   codigo: "NK", color: "#CCBB00" },
    { nombre: "Frontier Airlines", codigo: "F9", color: "#004225" },
  ];

  const servicios = [
    {
      icon: <Plane size={32} />,
      titulo: "Reservas de Vuelos",
      desc: "Reserve nuevos vuelos, modifique reservas existentes u obtenga ayuda para encontrar las mejores tarifas para sus fechas de viaje.",
    },
    {
      icon: <Clock size={32} />,
      titulo: "Estado del Vuelo y Actualizaciones",
      desc: "Obtenga información en tiempo real sobre salidas, llegadas, retrasos, cambios de puerta y cancelaciones.",
    },
    {
      icon: <Users size={32} />,
      titulo: "Cambios en la Reserva",
      desc: "Cambie sus fechas de viaje, actualice asientos, agregue pasajeros o modifique su itinerario con facilidad.",
    },
    {
      icon: <Shield size={32} />,
      titulo: "Cancelaciones y Reembolsos",
      desc: "Cancele su reserva y comprenda las políticas de reembolso, créditos y opciones de nueva reserva.",
    },
    {
      icon: <Headphones size={32} />,
      titulo: "Asistencia con Equipaje",
      desc: "Reporte equipaje perdido, rastree bolsas retrasadas u obtenga información sobre franquicias y tarifas de equipaje.",
    },
    {
      icon: <Globe size={32} />,
      titulo: "Información de Viaje",
      desc: "Conozca los requisitos de visa, políticas especiales, asistencia especial y guías de destino.",
    },
  ];

  const pasos = [
    {
      num: "01",
      titulo: "Llame Ahora",
      desc: `Marque ${TFN} para conectarse de inmediato con un agente de viajes en español.`,
    },
    {
      num: "02",
      titulo: "Explique su Necesidad",
      desc: "Indique si desea reservar, cambiar, cancelar o consultar sobre su vuelo. Nuestros agentes hablan español.",
    },
    {
      num: "03",
      titulo: "Reciba Asistencia Inmediata",
      desc: "Nuestros especialistas le ayudarán en tiempo real para resolver su consulta sin esperas innecesarias.",
    },
    {
      num: "04",
      titulo: "Confirme su Reserva",
      desc: "Recibirá su número de confirmación y detalles del vuelo directamente en su correo electrónico.",
    },
  ];

  const faqs = [
    {
      pregunta: "¿Cómo puedo hacer una reserva de aerolínea en español?",
      respuesta: `Puede llamar al ${TFN} para recibir asistencia completa en español. Nuestros agentes están disponibles las 24 horas, los 7 días de la semana, para ayudarle con reservas en todas las aerolíneas principales.`,
    },
    {
      pregunta: "¿Puedo cambiar o cancelar mi vuelo por teléfono?",
      respuesta: `Sí. Llame al ${TFN} y un agente le guiará a través del proceso de cambio o cancelación, explicándole las políticas de reembolso y las opciones disponibles.`,
    },
    {
      pregunta: "¿Atienden todas las aerolíneas?",
      respuesta: "Brindamos asistencia para todas las aerolíneas principales que operan en Estados Unidos y vuelos internacionales, incluyendo American Airlines, Delta, United, Southwest, JetBlue, Alaska, Spirit y Frontier.",
    },
    {
      pregunta: "¿Cuál es el horario de atención?",
      respuesta: `Nuestro servicio está disponible las 24 horas del día, los 7 días de la semana, incluidos fines de semana y días festivos. Llame al ${TFN} en cualquier momento.`,
    },
    {
      pregunta: "¿Cobran algún cargo por la llamada?",
      respuesta: `La llamada al ${TFN} es completamente gratuita desde cualquier teléfono dentro de los Estados Unidos. Es una línea toll-free sin costo para el usuario.`,
    },
    {
      pregunta: "¿Pueden ayudarme si perdí mi equipaje?",
      respuesta: "Sí. Podemos orientarle sobre cómo reportar equipaje perdido o retrasado, rastrear su bolsa y comunicarse con el departamento de equipaje de su aerolínea.",
    },
  ];

  const busquedas = [
    "reserva de aerolíneas en español",
    "número de teléfono de aerolínea en español",
    "servicio al cliente de aerolíneas en español",
    "reservar vuelo por teléfono español",
    "cancelar vuelo en español",
    "cambiar vuelo en español",
    "atención al cliente aerolíneas 24 horas",
    "agente de viajes en español",
    "reembolso de boleto de avión",
    "equipaje perdido aerolínea en español",
    "vuelos baratos reservar por teléfono",
    "asistencia de viaje en español",
  ];

  return (
    <>
      <Helmet>
        <title>Reserva de Aerolíneas en Español | Llame Ahora {TFN}</title>
        <meta
          name="description"
          content={`Reserva de aerolíneas en español. Llame al ${TFN} para asistencia 24/7 en español con reservas, cambios, cancelaciones y más. Agentes disponibles ahora.`}
        />
        <meta
          name="keywords"
          content="reserva de aerolíneas, vuelos en español, servicio al cliente aerolíneas español, reservar vuelo teléfono español, cancelar vuelo español"
        />
        <link rel="canonical" href="https://flightreservationcentre.com/reserva-de-aerolineas" />
        <meta property="og:title" content={`Reserva de Aerolíneas en Español | ${TFN}`} />
        <meta property="og:description" content={`Asistencia completa en español para reservas de vuelos. Llame al ${TFN} ahora.`} />
        <meta property="og:type" content="website" />
        <html lang="es" />
      </Helmet>

      <div className="rda-page">

        {/* ── HERO ── */}
        <section className="rda-hero">
          <div className="rda-hero-overlay" />
          <div className="rda-container rda-hero-content">
            <div className="rda-hero-badge">
              <span>🇪🇸 Servicio en Español</span>
            </div>
            <h1>
              Reserva de Aerolíneas<br />
              <span className="rda-hero-highlight">en Español</span>
            </h1>
            <p className="rda-hero-sub">
              Hable con un agente de viajes en español ahora mismo.<br />
              Reservas, cambios, cancelaciones y asistencia las <strong>24 horas, los 7 días</strong>.
            </p>

            <a href={`tel:${TFN_CLEAN}`} className="rda-hero-cta">
              <Phone size={26} />
              <div>
                <span className="rda-cta-label">Llame Gratis Ahora</span>
                <span className="rda-cta-number">{TFN}</span>
              </div>
            </a>

            <div className="rda-hero-trust">
              <span><CheckCircle size={16} /> Atención en Español</span>
              <span><CheckCircle size={16} /> Disponible 24/7</span>
              <span><CheckCircle size={16} /> Sin Costo de Llamada</span>
              <span><CheckCircle size={16} /> Todas las Aerolíneas</span>
            </div>
          </div>
        </section>

        {/* ── FLOATING CALL BAR ── */}
        <div className="rda-call-bar">
          <div className="rda-container rda-call-bar-inner">
            <div className="rda-call-bar-text">
              <AlertCircle size={18} />
              <span>¿Necesita ayuda con su vuelo? <strong>Agentes en español disponibles ahora</strong></span>
            </div>
            <a href={`tel:${TFN_CLEAN}`} className="rda-call-bar-btn">
              <Phone size={16} /> {TFN}
            </a>
          </div>
        </div>

        <main className="rda-main">
          <div className="rda-container">

            {/* ── SERVICIOS ── */}
            <section className="rda-section">
              <div className="rda-section-header">
                <h2>Servicios Disponibles en Español</h2>
                <p>Nuestros agentes bilingües están listos para ayudarle con todas sus necesidades de viaje aéreo.</p>
              </div>
              <div className="rda-services-grid">
                {servicios.map((s, i) => (
                  <div key={i} className="rda-service-card">
                    <div className="rda-service-icon">{s.icon}</div>
                    <h3>{s.titulo}</h3>
                    <p>{s.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── AEROLÍNEAS ── */}
            <section className="rda-section rda-airlines-section">
              <div className="rda-section-header">
                <h2>Aerolíneas que Atendemos</h2>
                <p>
                  Brindamos asistencia para todas las aerolíneas principales. Llame al{" "}
                  <strong>{TFN}</strong> para cualquiera de estas:
                </p>
              </div>
              <div className="rda-airlines-grid">
                {aerolineas.map((a, i) => (
                  <div key={i} className="rda-airline-card">
                    <div
                      className="rda-airline-logo"
                      style={{ backgroundColor: a.color }}
                    >
                      <span>{a.codigo}</span>
                    </div>
                    <p>{a.nombre}</p>
                  </div>
                ))}
              </div>
              <div className="rda-airlines-cta">
                <p>¿Su aerolínea no está en la lista? Podemos ayudarle de todas formas.</p>
                <a href={`tel:${TFN_CLEAN}`} className="rda-btn-secondary">
                  <Phone size={18} /> Llamar Ahora: {TFN}
                </a>
              </div>
            </section>

            {/* ── CÓMO FUNCIONA ── */}
            <section className="rda-section rda-steps-section">
              <div className="rda-section-header">
                <h2>¿Cómo Funciona?</h2>
                <p>Obtener ayuda en español es rápido y sencillo en 4 pasos.</p>
              </div>
              <div className="rda-steps-grid">
                {pasos.map((p, i) => (
                  <div key={i} className="rda-step-card">
                    <div className="rda-step-num">{p.num}</div>
                    <h3>{p.titulo}</h3>
                    <p>{p.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── WHY US ── */}
            <section className="rda-section rda-why-section">
              <div className="rda-why-grid">
                <div className="rda-why-text">
                  <h2>¿Por Qué Llamarnos para su Reserva?</h2>
                  <ul className="rda-why-list">
                    <li><CheckCircle size={20} className="rda-check" /> <div><strong>Atención en Español</strong> – Hable con agentes que entienden su idioma y cultura de viaje.</div></li>
                    <li><CheckCircle size={20} className="rda-check" /> <div><strong>Disponibilidad 24/7</strong> – Estamos aquí a cualquier hora, incluso en emergencias de último minuto.</div></li>
                    <li><CheckCircle size={20} className="rda-check" /> <div><strong>Sin Esperas Largas</strong> – Conecte directamente con un agente sin menús interminables.</div></li>
                    <li><CheckCircle size={20} className="rda-check" /> <div><strong>Llamada Gratuita</strong> – La línea es completamente toll-free desde EE.UU.</div></li>
                    <li><CheckCircle size={20} className="rda-check" /> <div><strong>Experiencia en Todas las Aerolíneas</strong> – Conocemos las políticas de cada aerolínea para darle la mejor orientación.</div></li>
                    <li><CheckCircle size={20} className="rda-check" /> <div><strong>Soluciones Rápidas</strong> – Resolvemos su problema en una sola llamada en la mayoría de los casos.</div></li>
                  </ul>
                </div>
                <div className="rda-why-cta-box">
                  <Star size={40} className="rda-star" />
                  <h3>Servicio de Confianza</h3>
                  <p>Miles de viajeros hispanohablantes confían en nosotros cada año para sus reservas aéreas.</p>
                  <a href={`tel:${TFN_CLEAN}`} className="rda-hero-cta rda-why-cta-btn">
                    <Phone size={22} />
                    <div>
                      <span className="rda-cta-label">Llame Ahora – Gratis</span>
                      <span className="rda-cta-number">{TFN}</span>
                    </div>
                  </a>
                </div>
              </div>
            </section>

            {/* ── FAQ ── */}
            <section className="rda-section">
              <div className="rda-section-header">
                <h2>Preguntas Frecuentes</h2>
                <p>Respuestas a las preguntas más comunes de nuestros clientes hispanohablantes.</p>
              </div>
              <div className="rda-faq-list">
                {faqs.map((f, i) => (
                  <div key={i} className="rda-faq-item">
                    <h3>{f.pregunta}</h3>
                    <p>{f.respuesta}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── BÚSQUEDAS COMUNES ── */}
            <section className="rda-section rda-searches-section">
              <h2>Búsquedas Frecuentes de Viajeros Hispanohablantes</h2>
              <p>
                Si está buscando ayuda con alguno de estos temas, llame al{" "}
                <strong>{TFN}</strong>:
              </p>
              <div className="rda-search-tags">
                {busquedas.map((b, i) => (
                  <span key={i} className="rda-search-tag">{b}</span>
                ))}
              </div>
            </section>

            {/* ── DISCLAIMER ── */}
            <section className="rda-section">
              <div className="rda-disclaimer-box">
                <h3>Aviso Importante</h3>
                <p>
                  <strong>Somos un servicio de asistencia de viajes independiente.</strong> No somos la línea oficial de atención al cliente de ninguna aerolínea específica. Brindamos orientación general de viaje, asistencia con reservas y apoyo para ayudar a los viajeros a tomar decisiones informadas. Para políticas oficiales de cada aerolínea, le recomendamos contactarlas directamente a través de sus sitios web oficiales.
                </p>
                <p>
                  Nuestro objetivo es ayudar a los viajeros hispanohablantes a navegar el complejo mundo de las reservas aéreas y proporcionar información precisa y útil para hacer su experiencia de viaje más fluida.
                </p>
              </div>
            </section>

          </div>
        </main>

        {/* ── FINAL CTA ── */}
        <section className="rda-final-cta">
          <div className="rda-container rda-final-cta-inner">
            <h2>¿Listo para Reservar su Vuelo en Español?</h2>
            <p>
              No pierda tiempo buscando números de teléfono o esperando en línea.<br />
              Nuestros especialistas en viajes están esperando su llamada ahora mismo.
            </p>
            <a href={`tel:${TFN_CLEAN}`} className="rda-hero-cta">
              <Phone size={28} />
              <div>
                <span className="rda-cta-label">Llame Gratis – 24/7 en Español</span>
                <span className="rda-cta-number">{TFN}</span>
              </div>
            </a>
            <div className="rda-final-features">
              <span>✓ Sin Espera</span>
              <span>✓ Agentes en Vivo</span>
              <span>✓ Todas las Aerolíneas</span>
              <span>✓ Servicio en Español</span>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default ReservaDeAerolineas;
